import { ApolloError, useApolloClient } from '@apollo/client';
import { isEmpty } from 'lodash';
import { MutableRefObject, useCallback, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import type { FieldErrors, SubmitHandler } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';
import { invalidateCacheObject } from '@/graphql/client.utils';
import { useReportError } from '@/hooks/useReportError';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { useDispositiveProvisionsContext } from '@/modules/dispositiveProvisions/contexts/dispositiveProvisions.context';
import { getEmptyDPMap } from '@/modules/dispositiveProvisions/dispositiveProvisions.utils';
import { makeActivateEntityUpdateInput } from '@/modules/entities/components/ConvertToActiveMenuItem/makeActivateEntityUpdateInput';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { useUpdateEntitiesMutation } from '@/modules/entities/graphql/UpdateEntities.generated';
import { EditTestamentaryEntitySection } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreen.types';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { Operations as DispositionsDiagramOperations } from '@/modules/entityDiagram/graphql/DispositionsDiagram.generated';
import {
  AiSuggestionAcceptanceStatus,
  AugmentedUpdateEntityInput,
  EntityStage,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import {
  CombinedFormShape,
  DispositionScheme,
} from '../dispositiveProvisions.types';
import {
  DispositiveProvisionsForm_DispositionScenario,
  DispositiveProvisionsFormShape,
} from '../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import {
  getCreateDispositiveProvisionsInput,
  NodesWithDispositiveProvisions,
} from '../DispositiveProvisionsForm/DispositiveProvisionsForm.utils';
import {
  DispositiveProvisionsPossibleRecipients_EntityFragment,
  useDispositiveProvisionsPossibleRecipientsQuery,
} from '../DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/graphql/DispositiveProvisionsFormRecipients.generated';
import {
  CreateDispositiveProvisionsAndAckSuggestionsV2Mutation,
  CreateDispositiveProvisionsForClientProfileMutation,
  CreateDispositiveProvisionsForTestamentaryEntitiesMutation,
  CreateDispositiveProvisionsMutation,
  useCreateDispositiveProvisionsAndAckSuggestionsForTestamentaryEntitiesMutation,
  useCreateDispositiveProvisionsAndAckSuggestionsV2Mutation,
  useCreateDispositiveProvisionsForClientProfileMutation,
  useCreateDispositiveProvisionsForTestamentaryEntitiesMutation,
  useCreateDispositiveProvisionsMutation,
} from '../DispositiveProvisionsForm/graphql/CreateDispositiveProvisions.generated';
import { Operations } from '../DispositiveProvisionsForm/graphql/GetDispositiveProvisions.generated';
import {
  DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE,
  DispositiveProvisionsFormV2Shape,
} from '../DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { convertDPFormV2ScenarioToDPFormV1Scenario } from '../DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.utils';

/**
 * Returns the appropriate diagram query to refetch based on the type of node
 * being edited. The diagram queries use `skip` based on target type, so only
 * the query corresponding to the current target will be active. Including
 * queries for other types would cause Apollo to log "Unknown query" warnings.
 */
export function getDiagramQueryToRefetch({
  isTestamentaryEntity,
  isClientProfile,
}: {
  isTestamentaryEntity: boolean;
  isClientProfile: boolean;
}) {
  if (isTestamentaryEntity) {
    return DispositionsDiagramOperations.Query
      .DispositionsDiagramTestamentaryEntities;
  }
  if (isClientProfile) {
    return DispositionsDiagramOperations.Query
      .DispositionsDiagramClientProfiles;
  }
  return DispositionsDiagramOperations.Query.DispositionsDiagram;
}

/**
 * Unique entity-recipient ids across the submitted scenarios. Keys off the
 * recipient-id PULID (as the save path does), not the form's `recipientKind`,
 * which is set by an async effect and can lag the id.
 */
export function getEntityRecipientIds(
  formValues: Record<
    DispositionScheme,
    DispositiveProvisionsForm_DispositionScenario | null
  >
): string[] {
  const entityRecipientIds = new Set<string>();
  for (const scenario of Object.values(formValues)) {
    for (const recipient of scenario?.recipients ?? []) {
      if (
        recipient.recipientId &&
        getPulidKind(recipient.recipientId) === PulidKind.Entity
      ) {
        entityRecipientIds.add(recipient.recipientId);
      }
    }
  }
  return Array.from(entityRecipientIds);
}

/**
 * Activation inputs for any entity recipients still in PRE_CREATED stage, so
 * the waterfall can route funds to them once active.
 */
function getPreCreatedRecipientActivationInputs(
  formValues: Record<
    DispositionScheme,
    DispositiveProvisionsForm_DispositionScenario | null
  >,
  possibleRecipientEntities: DispositiveProvisionsPossibleRecipients_EntityFragment[]
): AugmentedUpdateEntityInput[] {
  const entitiesById = new Map<
    string,
    DispositiveProvisionsPossibleRecipients_EntityFragment
  >();
  for (const entity of possibleRecipientEntities) {
    entitiesById.set(entity.id, entity);
  }

  const activationInputs: AugmentedUpdateEntityInput[] = [];

  for (const entityId of getEntityRecipientIds(formValues)) {
    const entity = entitiesById.get(entityId);
    if (entity?.stage !== EntityStage.PreCreated) {
      continue;
    }

    activationInputs.push(
      makeActivateEntityUpdateInput({
        entityId: entity.id,
        subtypeId: entity.subtype.id,
        entityType: getEntityTypeFromEntityKind(entity.kind),
      })
    );
  }

  return activationInputs;
}

interface UseSubmitDispositiveProvisionsProps {
  onAfterSubmit: (afterSubmitSection?: EditEntitySection) => void;
  onClose: () => void;
  /**
   * Called after any successful DP persist. Hosts (e.g. the presentation
   * designer) use it to flag that an edit landed so they refresh derived data
   * on close. See {@link DispositiveProvisionsFormProps.onDidSave}.
   */
  onDidSave?: (options: { isSwitchingTarget: boolean }) => void;
  /** Handler for section switching when editing testamentary entities */
  onTestamentaryEntitySectionSubmit?: (
    afterSubmitSection?: EditTestamentaryEntitySection
  ) => void;
}

export interface HandleDiagramTileClickSubmissionResult {
  success: boolean;
  errors?: FieldErrors<CombinedFormShape>;
  submit?: () => Promise<void>;
}

export function useSubmitDispositiveProvisions({
  onAfterSubmit,
  onClose,
  onDidSave,
  onTestamentaryEntitySectionSubmit,
}: UseSubmitDispositiveProvisionsProps) {
  // Apollo and misc helpers
  const client = useApolloClient();
  const { showFeedback } = useFeedback();
  const canEditDP = useCanEditDispositiveProvisions();
  const { reportError } = useReportError();

  // Contexts
  const {
    householdId,
    clientProfileOrEntityOrTestamentaryEntityId,
    isTestamentaryEntity,
    isClientProfile,
    isOnHypotheticalWaterfall,
    waterfall,
    entitySubtypeId,
    entityStage,
    entityType,
  } = useDispositiveProvisionsContext();

  const { refetchSuggestions } = useDispositiveProvisionSuggestionsContext();
  const { acknowledgedSuggestions } = useSuggestionAcknowledgementContext();

  // No onError handler so the awaited mutation rejects on failure (the catch in
  // onValidSubmission relies on this to abort).
  const [activateEntities] = useUpdateEntitiesMutation();

  // Recipient entities for the activation lookup, read cache-only from the
  // dropdown's query (already loaded by the time a recipient is selectable).
  const { data: possibleRecipientsData } =
    useDispositiveProvisionsPossibleRecipientsQuery({
      fetchPolicy: 'cache-only',
      variables: { where: { id: householdId }, hasDeadGrantors: false },
    });
  const possibleRecipientEntities = getNodes(
    getNodes(possibleRecipientsData?.households)[0]?.entities
  );

  // Form utilities
  const {
    handleSubmit,
    formState: { defaultValues, isSubmitting, isDirty },
    setShouldBlockNavigation,
    shouldBlockNavigation,
  } = useFormContext<CombinedFormShape>();

  /**
   * Used to track the next EditEntitySection to navigate to after a form submission.
   */
  const nextSectionRef = useRef<EditEntitySection | undefined>(undefined);

  /**
   * Used to track the next EditTestamentaryEntitySection to navigate to after a form submission.
   */
  const nextTestamentaryEntitySectionRef = useRef<
    EditTestamentaryEntitySection | undefined
  >(undefined);

  /**
   * Track whether this submission was triggered by a diagram tile switch.
   * When true, we should NOT close the modal after the mutation completes.
   */
  const skipCloseAfterSubmitRef = useRef(false);

  // Shared mutation options
  const mutationOptions = useMemo(() => {
    const diagramQueryToRefetch = getDiagramQueryToRefetch({
      isTestamentaryEntity,
      isClientProfile,
    });

    return {
      onError: (error: ApolloError) => {
        showFeedback(
          "We weren't able to save your dispositive provisions. Please refresh the page and try again."
        );
        reportError('could not create dispositive provisions', error);
        // Clear the refs
        nextSectionRef.current = undefined;
        nextTestamentaryEntitySectionRef.current = undefined;
        skipCloseAfterSubmitRef.current = false;
      },
      onCompleted: (
        _data:
          | CreateDispositiveProvisionsMutation
          | CreateDispositiveProvisionsAndAckSuggestionsV2Mutation
          | CreateDispositiveProvisionsForTestamentaryEntitiesMutation
          | CreateDispositiveProvisionsForClientProfileMutation
      ) => {
        showFeedback('Dispositive provisions created successfully', {
          variant: 'success',
        });
        // Signal a successful persist so hosts can refresh derived data (e.g.
        // the presentation designer's entity-overview context). Fires for every
        // successful variant since they all route through this shared handler.
        onDidSave?.({
          isSwitchingTarget: skipCloseAfterSubmitRef.current,
        });
        // Clear the refs
        nextSectionRef.current = undefined;
        nextTestamentaryEntitySectionRef.current = undefined;
        skipCloseAfterSubmitRef.current = false;
      },
      refetchQueries: [
        Operations.Query.GetDispositiveProvisions,
        diagramQueryToRefetch,
      ],
    };
  }, [
    isClientProfile,
    isTestamentaryEntity,
    onDidSave,
    reportError,
    showFeedback,
  ]);

  /* =================================================================== */
  /*                           MUTATION HOOKS                            */
  /* =================================================================== */

  /* ------------------------- ENTITY MUTATIONS ------------------------- */

  // Creates dispositive provisions for an entity (no suggestion acknowledgement)
  const [setDispositiveProvisions] = useCreateDispositiveProvisionsMutation({
    ...mutationOptions,
    onCompleted: async (data) => {
      const skipClose = skipCloseAfterSubmitRef.current;
      const nextSection = nextSectionRef.current;
      mutationOptions.onCompleted(data);
      await invalidateCacheObject(data.updateEntity, client);

      diagnostics.debug(
        `[useSubmitDispositiveProvisions][setDispositiveProvisions][onCompleted] calling onSwitchSectionAfterSubmit, nextSection=${nextSection}`
      );
      if (!skipClose) {
        onAfterSubmit(nextSection);
      }
      // Reset
      skipCloseAfterSubmitRef.current = false;
    },
  });

  // Saves entity dispositions and acknowledges matched suggestions in one mutation.
  const [setDispositiveProvisionsAndSuggestionsV2] =
    useCreateDispositiveProvisionsAndAckSuggestionsV2Mutation({
      ...mutationOptions,
      onCompleted: async (data) => {
        const skipClose = skipCloseAfterSubmitRef.current;
        const nextSection = nextSectionRef.current;
        mutationOptions.onCompleted(data);
        await invalidateCacheObject(data.updateEntity, client);
        if (!skipClose) {
          void refetchSuggestions(data.updateEntity.id);
        }

        diagnostics.debug(
          `[useSubmitDispositiveProvisions][setDispositiveProvisionsAndSuggestionsV2][onCompleted] calling onSwitchSectionAfterSubmit, nextSection=${nextSection}`
        );
        if (!skipClose) {
          onAfterSubmit(nextSection);
        }
        skipCloseAfterSubmitRef.current = false;
      },
    });

  /* ------------------- TESTAMENTARY ENTITY MUTATIONS ------------------ */

  /**
   * Handles post-mutation navigation for testamentary entity submissions.
   * Checks both TE section refs and entity section refs (for death order changes).
   * Values must be snapshotted before calling mutationOptions.onCompleted which clears the refs.
   */
  const handleTestamentaryEntityPostSubmitNavigation = (
    skipClose: boolean,
    nextTESection: EditTestamentaryEntitySection | undefined,
    nextEntitySection: EditEntitySection | undefined
  ) => {
    if (!skipClose) {
      if (nextTESection && onTestamentaryEntitySectionSubmit) {
        onTestamentaryEntitySectionSubmit(nextTESection);
      } else if (nextEntitySection) {
        // Death order changes pass EditEntitySection values through onAfterSubmit
        onAfterSubmit(nextEntitySection);
      } else {
        onClose();
      }
    }
    skipCloseAfterSubmitRef.current = false;
  };

  // Creates dispositive provisions for a testamentary entity (no suggestion acknowledgement)
  const [setDispositiveProvisionsForTestamentaryEntities] =
    useCreateDispositiveProvisionsForTestamentaryEntitiesMutation({
      ...mutationOptions,
      onCompleted: async (data) => {
        // Snapshot refs before mutationOptions.onCompleted clears them
        const skipClose = skipCloseAfterSubmitRef.current;
        const nextTESection = nextTestamentaryEntitySectionRef.current;
        const nextEntitySection = nextSectionRef.current;

        mutationOptions.onCompleted(data);
        await invalidateCacheObject(data.updateTestamentaryEntity, client);
        handleTestamentaryEntityPostSubmitNavigation(
          skipClose,
          nextTESection,
          nextEntitySection
        );
      },
    });

  // Creates dispositive provisions for a testamentary entity AND acknowledges suggestions
  const [setDispositiveProvisionsAndSuggestionsForTestamentaryEntities] =
    useCreateDispositiveProvisionsAndAckSuggestionsForTestamentaryEntitiesMutation(
      {
        ...mutationOptions,
        onCompleted: async (data) => {
          // Snapshot refs before mutationOptions.onCompleted clears them
          const skipClose = skipCloseAfterSubmitRef.current;
          const nextTESection = nextTestamentaryEntitySectionRef.current;
          const nextEntitySection = nextSectionRef.current;

          mutationOptions.onCompleted(data);
          await invalidateCacheObject(data.updateTestamentaryEntity, client);
          if (!skipClose) {
            // Only refetch suggestions if we are not switching tiles.
            void refetchSuggestions(data.updateTestamentaryEntity.id);
          }
          handleTestamentaryEntityPostSubmitNavigation(
            skipClose,
            nextTESection,
            nextEntitySection
          );
        },
      }
    );

  /* ---------------------- CLIENT PROFILE MUTATIONS -------------------- */

  // Creates dispositive provisions for a client profile (currently no suggestion acknowledgement variant)
  const [setDispositiveProvisionsForClientProfile] =
    useCreateDispositiveProvisionsForClientProfileMutation({
      ...mutationOptions,
      onCompleted: async (data) => {
        const skipClose = skipCloseAfterSubmitRef.current;
        const clientProfileId = clientProfileOrEntityOrTestamentaryEntityId;
        mutationOptions.onCompleted(data);
        // Invalidate the ClientProfile cache object as the response does not contain it.
        await invalidateCacheObject(
          { __typename: 'ClientProfile', id: clientProfileId },
          client
        );

        if (!skipClose) {
          onClose();
        }
        skipCloseAfterSubmitRef.current = false;
      },
    });

  /* ---------------------- FORM SUBMISSION LOGIC ---------------------- */

  const onValidSubmission: SubmitHandler<CombinedFormShape> = async (
    values
  ) => {
    if (!canEditDP) return;
    flushSync(() => {
      setShouldBlockNavigation(false);
    });

    if (!clientProfileOrEntityOrTestamentaryEntityId) {
      throw new Error(
        'Cannot create dispositive provisions without the necessary client profile id, entity id, or testamentary entity id'
      );
    }

    // Map the V1 and V2 form values into a common format, where each
    // disposition scheme is mapped to its corresponding form values.
    const formValues = normalizeFormValues(values);
    const initialFormValues = defaultValues
      ? normalizeFormValues(
          defaultValues as
            | DispositiveProvisionsFormShape
            | DispositiveProvisionsFormV2Shape
        )
      : getEmptyDPMap();

    // Activate any PRE_CREATED entity recipients before saving so the waterfall
    // can route funds to them.
    const activationInputs = getPreCreatedRecipientActivationInputs(
      formValues,
      possibleRecipientEntities
    );
    if (activationInputs.length > 0) {
      try {
        await activateEntities({ variables: { input: activationInputs } });
      } catch (error) {
        showFeedback(
          "We weren't able to save your dispositive provisions. Please refresh the page and try again."
        );
        reportError(
          'could not activate pre-created entity recipients',
          error instanceof Error ? error : undefined
        );
        // Re-throw so the tile-switch flow doesn't treat a failed save as
        // success and drop the unsaved provisions.
        throw error;
      }
    }

    if (isTestamentaryEntity) {
      const { type, input } = getCreateDispositiveProvisionsInput(
        formValues,
        initialFormValues,
        {
          testamentaryEntityId: clientProfileOrEntityOrTestamentaryEntityId,
          isOnHypotheticalWaterfall,
          waterfallId: waterfall?.id,
        },
        householdId
      );

      if (type !== NodesWithDispositiveProvisions.TestamentaryEntity) {
        throw new Error('expected input type for testamentary entity');
      }

      if (!isEmpty(acknowledgedSuggestions)) {
        return setDispositiveProvisionsAndSuggestionsForTestamentaryEntities({
          variables: {
            testamentaryEntityInput: input,
            acknowledgeSuggestionsInput: Object.values(
              acknowledgedSuggestions
            ).map((s) => ({
              suggestionID: s.suggestionID,
              status: s.status,
              clientProfileID: s.clientProfileId,
              clientOrganizationID: s.clientOrganizationId,
              entityID: s.entityId,
              testamentaryEntityID: s.testamentaryEntityId,
            })),
          },
        });
      }

      return setDispositiveProvisionsForTestamentaryEntities({
        variables: { input },
      });
    }

    if (isClientProfile) {
      const { type, input } = getCreateDispositiveProvisionsInput(
        formValues,
        initialFormValues,
        {
          householdId: householdId,
          clientProfileId: clientProfileOrEntityOrTestamentaryEntityId,
          isOnHypotheticalWaterfall,
          waterfallId: waterfall?.id,
        },
        householdId
      );

      if (type !== NodesWithDispositiveProvisions.ClientProfile) {
        throw new Error('expected input type for client profile');
      }

      return setDispositiveProvisionsForClientProfile({
        variables: { input },
      });
    }

    // ENTITY
    if (!entitySubtypeId || !entityType) {
      throw new Error(
        'Cannot create dispositive provisions without entitySubtypeId and entityType'
      );
    }

    const { type, input } = getCreateDispositiveProvisionsInput(
      formValues,
      initialFormValues,
      {
        entityId: clientProfileOrEntityOrTestamentaryEntityId,
        entitySubtypeId,
        entityType,
        isOnHypotheticalWaterfall,
        waterfallId: waterfall?.id,
      },
      householdId
    );

    if (type !== NodesWithDispositiveProvisions.Entity) {
      throw new Error('expected input type for entity');
    }

    const acknowledgeSuggestionsInput = Object.values(
      acknowledgedSuggestions
    ).map((s) => ({
      suggestionID: s.suggestionID,
      status: s.status,
      clientProfileID: s.clientProfileId,
      clientOrganizationID: s.clientOrganizationId,
      entityID: s.entityId,
      testamentaryEntityID: s.testamentaryEntityId,
    }));

    // When accepting DP suggestions for a pre-created entity, activate it.
    // This mirrors the pattern in AddHoldingsFormModal where pre-created
    // entities are activated when holdings are added from suggestions.
    const hasAcceptedSuggestions = acknowledgeSuggestionsInput.some(
      (s) =>
        s.status === AiSuggestionAcceptanceStatus.Accepted ||
        s.status === AiSuggestionAcceptanceStatus.AutoAccepted
    );
    if (entityStage === EntityStage.PreCreated && hasAcceptedSuggestions) {
      input.update = {
        ...input.update,
        stage: EntityStage.Active,
      };
    }

    if (!isEmpty(acknowledgedSuggestions)) {
      return setDispositiveProvisionsAndSuggestionsV2({
        variables: {
          entityInput: input,
          acknowledgeSuggestionsInput,
        },
      });
    }

    return setDispositiveProvisions({
      variables: { input },
    });
  };

  const onSubmit = handleSubmit(onValidSubmission);

  /**
   * Direct submission handler for footer buttons. Clears any stale section refs
   * to prevent unexpected navigation if a previous section switch failed validation.
   */
  const onSubmitDirect = useCallback(() => {
    nextSectionRef.current = undefined;
    nextTestamentaryEntitySectionRef.current = undefined;
    return onSubmit();
  }, [onSubmit]);

  /* ----------------- SPLIT SCREEN MODAL SECTION SUBMIT HANDLERS ------------------ */

  /**
   * Base handler for split screen modal section switching.
   * Saves the form before navigating if there are unsaved changes.
   */
  const handleSplitScreenSubmissionBase = useCallback(
    <T>(
      afterSubmitSection: T | undefined,
      onAfterSubmitCallback: ((section?: T) => void) | undefined,
      sectionRef: MutableRefObject<T | undefined>,
      debugLabel: string
    ) => {
      // Ensure we DO close / navigate after this submission.
      skipCloseAfterSubmitRef.current = false;
      diagnostics.debug(
        `[${debugLabel}] handling split screen submit, afterSubmitSection=${afterSubmitSection}, shouldBlockNavigation=${shouldBlockNavigation}, isDirty=${isDirty}`
      );

      if (!canEditDP || (!isDirty && !shouldBlockNavigation)) {
        diagnostics.debug(`[${debugLabel}] nothing to submit, returning`);
        onAfterSubmitCallback?.(afterSubmitSection);
        return;
      }

      sectionRef.current = afterSubmitSection;
      return onSubmit();
    },
    [canEditDP, isDirty, onSubmit, shouldBlockNavigation]
  );

  /**
   * Submission handler for EditEntitySplitScreenModal's section switcher.
   */
  const handleEditEntitySplitScreenSubmission = useCallback(
    (afterSubmitSection?: EditEntitySection) =>
      handleSplitScreenSubmissionBase(
        afterSubmitSection,
        onAfterSubmit,
        nextSectionRef,
        'handleEditEntitySplitScreenSubmission'
      ),
    [handleSplitScreenSubmissionBase, onAfterSubmit]
  );

  /**
   * Submission handler for EditTestamentaryEntitySplitScreenModal's section switcher.
   */
  const handleTestamentaryEntitySplitScreenSubmission = useCallback(
    (afterSubmitSection?: EditTestamentaryEntitySection) =>
      handleSplitScreenSubmissionBase(
        afterSubmitSection,
        onTestamentaryEntitySectionSubmit,
        nextTestamentaryEntitySectionRef,
        'handleTestamentaryEntitySplitScreenSubmission'
      ),
    [handleSplitScreenSubmissionBase, onTestamentaryEntitySectionSubmit]
  );

  /* ----------------- HANDLER TO SWITCH THE SELECTED DIAGRAM TILE FORM SUBMISSION ------------------ */

  const handleDiagramTileClickSubmission =
    useCallback(async (): Promise<HandleDiagramTileClickSubmissionResult> => {
      if (!canEditDP) return { success: true };
      const submit = async () => {
        // Mark that we should NOT close the modal when this submission resolves.
        skipCloseAfterSubmitRef.current = true;
        diagnostics.debug(`[handleDiagramTileClickSubmission]`, {
          shouldBlockNavigation,
          isDirty,
        });

        if (!isDirty && !shouldBlockNavigation) {
          // No pending changes – caller will handle the switch after awaiting.
          diagnostics.debug(
            '[handleDiagramTileClickSubmission] nothing to submit, returning'
          );
          return;
        }

        await onSubmit();
      };

      return new Promise<HandleDiagramTileClickSubmissionResult>((resolve) => {
        const submitHandler = handleSubmit(
          () => {
            resolve({
              success: true,
              submit,
            });
          },
          (validationErrors) => {
            resolve({
              success: false,
              errors: validationErrors,
            });
          }
        );

        void submitHandler();
      });
    }, [canEditDP, shouldBlockNavigation, isDirty, onSubmit, handleSubmit]);

  return {
    /** Use onSubmit for direct submissions (e.g., footer buttons). Clears stale section refs. */
    onSubmit: onSubmitDirect,
    handleEditEntitySplitScreenSubmission,
    handleTestamentaryEntitySplitScreenSubmission,
    handleDiagramTileClickSubmission,
    isSubmitting,
  } as const;
}

/**
 * Normalizes the form values from either the V1 or V2 form data, returning
 * a map of disposition schemes to their corresponding disposition scenario(s).
 * This normalized format is used to create the input for the mutation.
 *
 * @param values
 */
function normalizeFormValues(
  values: DispositiveProvisionsFormShape | DispositiveProvisionsFormV2Shape
): Record<
  DispositionScheme,
  DispositiveProvisionsForm_DispositionScenario | null
> {
  const dpScenario = (values as DispositiveProvisionsFormV2Shape)[
    DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE
  ];
  const { firstDeath, secondDeath } = dpScenario;

  return {
    [DispositionScheme.UPON_FIRST_DEATH]:
      convertDPFormV2ScenarioToDPFormV1Scenario(dpScenario, firstDeath),
    [DispositionScheme.UPON_SECOND_DEATH]: secondDeath
      ? convertDPFormV2ScenarioToDPFormV1Scenario(dpScenario, secondDeath)
      : null,
    // The V2 forms will never provide a disposition scheme of NONE, because
    // the form allows the user to add/remove recipients from both death events.
    [DispositionScheme.NONE]: null,
  };
}
